import type { TowerDesign } from "@/game/towerSpec";
import type { Material } from "@/physics/model";
import { EIFFEL_MESH } from "@/game/meshdata/eiffel";

/**
 * EIFFEL TOWER — Paris, 1889. Built DIRECTLY from a real Eiffel Tower 3D mesh
 * (github jmorris335 "Eiffel Tower 760" STL, parsed + deduped + scaled to 300 m),
 * NOT a parametric lattice. The mesh's 1,140 edges ARE the tower's members, in
 * their real positions — the flared legs, the base arches, the platforms and the
 * spire come from the model geometry itself, not from an interpretation.
 *
 * Material: puddled wrought iron (fer puddlé) — E ≈ 190 GPa, yield ~190 MPa,
 * density ~7740 kg/m³ (NOT modern steel). ~7,300 t of iron in the real tower.
 */

// Each mesh edge represents a whole braced iron GIRDER, not a single bar, so its
// effective axial capacity is far above the base metal's 190 MPa. Density (→mass)
// stays that of real wrought iron; only the effective yield is raised so the
// coarse-mesh struts don't shed under self-weight.
const EIFFEL_GIRDER: Material = { name: "Eiffel iron girder (effective)", E: 190e9, Fy: 700e6, rho: 7740 };

export const design: TowerDesign = {
  id: "eiffel-tower",
  name: "Eiffel Tower (Paris)",
  description: "300 m puddled-iron lattice, 1889 — geometry from a real Eiffel STL mesh.",
  author: "iconic tower — geometry parsed from a real Eiffel Tower STL mesh",
  spec: {
    type: "lattice",
    height: 300,
    mesh: {
      nodes: EIFFEL_MESH.nodes,
      edges: EIFFEL_MESH.edges,
      // representative wrought-iron member. The coarse mesh funnels the tower's
      // ~72 MN weight through few base struts, so they must be stout to STAND
      // (util < 0.9) — larger than the real per-member area, so total mass runs
      // above the real ~7,300 t. High I (built-up box) also stops the long struts
      // buckling. (Real Eiffel legs are braced lattice girders, not solid struts.)
      section: { name: "Eiffel iron member", A: 0.036, I: 0.03, dragWidth: 0.4 },
      material: EIFFEL_GIRDER,
      // found the four splayed legs over their lower run (they meet at the first
      // platform), spreading the ~72 MN reaction across many base members instead
      // of concentrating it — otherwise the coarse mesh overloads a few struts.
      baseFixedBelow: 60,
    },
    foundation: {
      soil: "dense-sand", // Champ de Mars alluvium
      base: { type: "pad", width: 15, diameter: 2, depth: 5, concrete: "c20" },
    },
  },
};
